var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import * as userAgent from "@play-co/user-agent";
import { internalAnalytics } from "./analytics/index.mjs";
import { trackPlatformInitFailed, trackInviteAsync, trackInviteAsyncSuccess, trackInviteAsyncFailure, trackShareStoryAsync, trackShareStoryAsyncSuccess, trackShareStoryAsyncFailure, trackShareLinkAsync, trackShareLinkAsyncSuccess, trackShareLinkAsyncFailure, trackUpdateAsyncSuccess, trackUpdateAsyncFailed, trackPayloadReadFailure } from "./analytics/events.mjs";
import { excludePayloadOnlyProperties } from "./analytics/utils.mjs";
import { withTiming } from "./asyncUtils.mjs";
import { copyToClipboard } from "./browserUtils.mjs";
import { removeTimestepBuildSplashScreen } from "./domUtils.mjs";
import { createError } from "./errorUtils.mjs";
import getQueryParameters from "./getQueryParameters.mjs";
import { Platform } from "./Platform.mjs";
class PlatformTelegram extends Platform {
  constructor() {
    super("telegram");
    __publicField(this, "adEntryPayload");
    __publicField(this, "telegramAuthorizationData");
    __publicField(this, "_hasPremium", false);
  }
  /**
   * Whether this user has subscribed to Telegram Premium.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get hasPremium() {
    this.throwIfNotInitialized();
    return this._hasPremium;
  }
  /**
   * Gets the Telegram authorization data for use with Replicant.
   *
   * Will throw an error if `initializeAsync` hasn't finished resolving.
   *
   * Example:
   *
   * ```typescript
   * const replicant = createClientReplicant({
   *   telegramAuthorizationData: platform.getTelegramAuthorizationData(),
   * });
   * ```
   */
  getTelegramAuthorizationData() {
    this.throwIfNotInitialized();
    return this.telegramAuthorizationData;
  }
  /**
   * Gets the Telegram bot's username.
   *
   * @throws If `initializeAsync` hasn't finished resolving, or if the bot username is
   * misconfigured.
   */
  getTelegramBotUsername() {
    this.throwIfNotInitialized();
    const telegramBotUsername = this._gameConfig.telegramBotUsername;
    if (!telegramBotUsername) {
      throw new Error(
        "Telegram bot username is missing, define TELEGRAM_BOT_USERNAME or include in config"
      );
    }
    return telegramBotUsername;
  }
  async dismissLoadingScreenAsync() {
    removeTimestepBuildSplashScreen();
  }
  async initializeAsync(config) {
    if (this._initialization == null) {
      this._initialization = this.doInit(config);
    }
    return this._initialization;
  }
  async doInit(config) {
    this.readConfiguration(config);
    internalAnalytics.initialize({
      ...this._gameConfig,
      appID: this.appID,
      // this is to work around years-old technical debt related to appID sometimes being an object in the config
      convertCurrency: (...args) => this.convertCurrency(...args)
    });
    try {
      if (process.env.NODE_ENV !== "test" && typeof Telegram === "undefined") {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://telegram.org/js/telegram-web-app.js";
          script.addEventListener("load", () => {
            resolve();
          });
          script.addEventListener("error", (event) => {
            reject(event.error);
          });
          document.body.appendChild(script);
        });
      }
      if (typeof Telegram === "undefined") {
        throw new Error("Telegram SDK is not available");
      }
      const initData = Telegram.WebApp.initDataUnsafe;
      const telegramUser = initData.user;
      if (!telegramUser) {
        throw new Error("Telegram user is not authenticated");
      }
      await this._initialize("" + telegramUser.id);
      if (telegramUser.first_name) {
        this.playerName = telegramUser.first_name;
      }
      if (telegramUser.photo_url) {
        this.playerPhoto = telegramUser.photo_url;
      }
      if (telegramUser.language_code) {
        this.platformLocale = telegramUser.language_code;
      }
      if (telegramUser.username) {
        internalAnalytics.setUserProperties({
          lastEntryTelegramUsername: telegramUser.username
        });
        this.username = telegramUser.username;
      }
      if (Telegram.WebApp.version) {
        internalAnalytics.setUserProperties({
          lastEntryTelegramBotAPIVersion: Telegram.WebApp.version
        });
      }
      this._hasPremium = !!telegramUser.is_premium;
      this._setIsSubscribedToBot(!!telegramUser.allows_write_to_pm);
      this.telegramAuthorizationData = Object.fromEntries(
        Telegram.WebApp.initData.split("&").map((part) => part.split("="))
      );
    } catch (error) {
      trackPlatformInitFailed(error);
      throw error;
    }
    this.trackTimeTo("initializeAsync");
    this.resolveFriendsAvailable();
    this.osType = userAgent.os.isAndroid ? "ANDROID" : userAgent.os.isIOS ? "IOS" : "WEB";
    this.isMobile = this.osType !== "WEB";
  }
  // Include Telegram-specific entry event properties
  _populateEntryDataEventProperties(eventProperties) {
    super._populateEntryDataEventProperties(eventProperties);
    const queryParams = getQueryParameters();
    eventProperties.telegramStartApp = queryParams.startapp;
    eventProperties.telegramStartAttach = queryParams.startattach;
    eventProperties.telegramStartParam = Telegram.WebApp.initDataUnsafe.start_param;
    const coercedInitData = Telegram.WebApp.initDataUnsafe;
    eventProperties.telegramChatInstance = coercedInitData.chat_instance;
    eventProperties.telegramChatType = coercedInitData.chat_type;
    eventProperties.sourceLastEntryHasTelegramPremium = this.entryData.lastEntryHasTelegramPremium;
    eventProperties.sourceLastEntryTelegramUsername = this.entryData.lastEntryTelegramUsername;
  }
  // Include Telegram-specific share payload properties
  getPlatformPayloadData() {
    const lastProps = internalAnalytics.getUserProperties();
    return {
      ...super.getPlatformPayloadData(),
      lastEntryHasTelegramPremium: this._hasPremium,
      lastEntryTelegramUsername: lastProps.lastEntryTelegramUsername
    };
  }
  // Include Telegram-specific user properties
  sendEntryFinalAnalytics(entryFinalEventProperties, firstEntryUserProperties, lastEntryUserProperties) {
    return super.sendEntryFinalAnalytics(
      entryFinalEventProperties,
      {
        ...firstEntryUserProperties,
        firstEntryHasTelegramPremium: this._hasPremium
      },
      {
        ...lastEntryUserProperties,
        lastEntryHasTelegramPremium: this._hasPremium
      }
    );
  }
  /**
   * Create a manually shareable t.me invite link.
   *
   * Most users should instead use `inviteAsync`, but this can be used to populate an in-game UI element.
   * Use `shareLinkAsync` to copy an invite link to clipboard.
   *
   * You can pass a custom analytics channel, which defaults to `LINK`.
   *
   * Note that the `text` option of `InviteAsyncOptions` does not apply to this function.
   *
   * As with `inviteAsync`, you must have a custom data codec set (for example with `configureExtensions`).
   */
  getInviteLink(opts) {
    const payloadData = this._populateNotificationPayload(
      opts.data,
      opts.channel ?? "LINK"
    );
    return this.createInviteLinkFromPayload({
      botUsername: opts.telegramBotUsername,
      miniAppShortName: opts.telegramMiniAppName,
      payloadData
    });
  }
  createInviteLinkFromPayload(opts) {
    const encodedPayloadKey = this.dataCodec.encode(opts.payloadData).$key;
    if (!encodedPayloadKey) {
      throw new Error("Failed to encode Telegram link payload");
    }
    if (encodedPayloadKey.length > 64) {
      throw new Error(
        `Telegram link payload of ${encodedPayloadKey} exceeds Telegram limit of 64 characters`
      );
    }
    const botUsername = opts.botUsername ?? this.getTelegramBotUsername();
    const playLink = new URL(
      `https://t.me/${botUsername}/${opts.miniAppShortName}`
    );
    playLink.searchParams.set("startapp", encodedPayloadKey);
    return playLink.href;
  }
  async inviteAsync(opts) {
    const payloadData = this._populateNotificationPayload(opts.data, "INVITE");
    const analyticsData = excludePayloadOnlyProperties(payloadData);
    trackInviteAsync(analyticsData);
    const doShare = async () => {
      var _a;
      const shareIntent = new URL("https://t.me/share/url");
      const playLink = this.createInviteLinkFromPayload({
        botUsername: opts.telegramBotUsername,
        miniAppShortName: opts.telegramMiniAppName,
        payloadData
      });
      shareIntent.searchParams.set("url", playLink);
      const text = typeof opts.text === "string" ? opts.text : (_a = opts.text) == null ? void 0 : _a.default;
      const textQuerySuffix = text ? `&text=${encodeURIComponent(text)}` : "";
      Telegram.WebApp.openTelegramLink(shareIntent.href + textQuerySuffix);
    };
    return withTiming(doShare).then(({ requestMs }) => {
      trackInviteAsyncSuccess({ ...analyticsData, requestMs });
    }).catch(({ error, requestMs }) => {
      trackInviteAsyncFailure(error, { ...analyticsData, requestMs });
      return Promise.reject(error);
    });
  }
  async shareStoryAsync(opts) {
    const payloadData = {
      ...this._populateNotificationPayload(opts.data, "STORY"),
      creativeCTA: opts.cta
    };
    const playLink = this.createInviteLinkFromPayload({
      botUsername: opts.telegramBotUsername,
      miniAppShortName: opts.telegramMiniAppName,
      payloadData
    });
    const analyticsData = excludePayloadOnlyProperties(payloadData);
    trackShareStoryAsync(analyticsData);
    try {
      if (!Telegram.WebApp.shareToStory) {
        throw createError(
          "INVALID_OPERATION",
          "Telegram.WebApp.shareToStory is undefined"
        );
      }
      Telegram.WebApp.shareToStory(opts.image, {
        text: opts.text,
        widget_link: { url: playLink, name: opts.cta }
        // Only premium subscribers can post stories with links so this property gets ignored in case of no premium
      });
      trackShareStoryAsyncSuccess(analyticsData);
    } catch (error) {
      trackShareStoryAsyncFailure(error, analyticsData);
      throw error;
    }
  }
  async shareLinkAsync(opts) {
    const payloadData = this._populateNotificationPayload(opts.data, "LINK");
    const analyticsData = excludePayloadOnlyProperties(payloadData);
    trackShareLinkAsync(analyticsData);
    const inviteLink = this.getInviteLink(opts);
    return withTiming(() => copyToClipboard(inviteLink)).then(({ requestMs }) => {
      trackShareLinkAsyncSuccess({ ...analyticsData, requestMs });
    }).catch(({ error, requestMs }) => {
      trackShareLinkAsyncFailure(error, {
        ...analyticsData,
        requestMs
      });
      throw error;
    });
  }
  async updateAsync(opts) {
    const payloadData = {
      ...this._populateNotificationPayload(opts.data, "UPDATE"),
      receiverID: opts.receiver.id,
      receiverTelegramUsername: opts.receiver.username
    };
    const analyticsData = excludePayloadOnlyProperties(payloadData);
    try {
      const { $channel, receiverID } = this.entryData;
      const referrerId = this.getReferrerID();
      const alreadyInChatThreadWithReceiver = $channel === "UPDATE" && (referrerId === this.playerID && receiverID === opts.receiver.id || // Entry point is a message sent by the current player to the update receiver
      referrerId === opts.receiver.id && receiverID === this.playerID);
      if (alreadyInChatThreadWithReceiver) {
        throw createError(
          "INVALID_PARAM",
          "Already in chat thread with receiver"
        );
      }
      const playLink = this.createInviteLinkFromPayload({
        botUsername: opts.telegramBotUsername,
        miniAppShortName: opts.telegramMiniAppName,
        payloadData
      });
      const message = typeof opts.text === "string" && opts.text ? `${opts.text} ${playLink}` : playLink;
      const messageIntent = `https://t.me/${encodeURI(
        opts.receiver.username
      )}?text=${encodeURIComponent(message)}`;
      Telegram.WebApp.openTelegramLink(messageIntent);
      trackUpdateAsyncSuccess(analyticsData);
      void this.sendRawAnalyticsEvent({
        eventName: "UpdateReceiptSuccess",
        eventProperties: analyticsData,
        userID: opts.receiver.id,
        userProperties: opts.receiver.userProperties
      }).catch(() => void 0);
      return { payloadData, returnValue: void 0 };
    } catch (error) {
      trackUpdateAsyncFailed(error, analyticsData);
      return Promise.reject(error);
    }
  }
  async startGameAsyncInner() {
    Telegram.WebApp.ready();
    this.trackTimeTo("startGameAsync");
    await this._prepareEntryData();
    const url = new URL(location.href);
    if (url.searchParams.has("payload")) {
      url.searchParams.delete("payload");
      history.replaceState(null, "", url.href);
    }
  }
  setAdEntryPayload(payload) {
    this.adEntryPayload = payload;
  }
  _getEntryPointDataForce() {
    if (this.adEntryPayload) {
      return this.adEntryPayload;
    }
    const telegramStartParam = Telegram.WebApp.initDataUnsafe.start_param;
    if (telegramStartParam) {
      return { $key: telegramStartParam };
    }
    try {
      const { payload } = getQueryParameters();
      if (payload) {
        return JSON.parse(payload);
      }
    } catch (error) {
      trackPayloadReadFailure(error, {
        rawPayload: window.location.search,
        type: "parseFailed"
      });
    }
    return {};
  }
}
export {
  PlatformTelegram
};
